import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "pj_theme";
const MODES = ["dark", "light", "system"];

const ThemeCtx = createContext({ mode: "dark", resolved: "dark", setMode: () => {} });
export const useTheme = () => useContext(ThemeCtx);

const systemPrefersLight = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches;

const readMode = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return MODES.includes(stored) ? stored : "dark";
};

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readMode);
  const [systemLight, setSystemLight] = useState(systemPrefersLight);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: light)");
    if (!mq) return;
    const onChange = (e) => setSystemLight(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved = mode === "system" ? (systemLight ? "light" : "dark") : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("theme-light", resolved === "light");
    root.style.colorScheme = resolved;
  }, [resolved]);

  const setMode = useCallback((next) => {
    if (!MODES.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setModeState(next);
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}
